import { NODE_TYPE_ENTITY, NODE_TYPE_INVESTMENT, NODE_TYPE_STRUCTURED } from "@/constants/lexical";
import type { EditorState } from "../state";

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as UnknownRecord;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function getNodeData(nodeRecord: UnknownRecord): UnknownRecord {
  const data = asRecord(nodeRecord.data);
  return data ?? nodeRecord;
}

function getNodeChildren(nodeRecord: UnknownRecord, nodeData: UnknownRecord): unknown[] {
  if (Array.isArray(nodeData.children)) {
    return nodeData.children;
  }
  if (Array.isArray(nodeRecord.children)) {
    return nodeRecord.children;
  }
  return [];
}

/**
 * Check if Content Schema has any significant nodes (not just empty structure)
 * This allows saving memos with only special nodes like Self-Contract
 * ONLY supports Content Schema format: { type: "root", version: 1, children: [...] }
 */
function hasSignificantNodes(content: string): boolean {
  try {
    // Handle empty or invalid content
    if (!content || content.trim() === "") {
      return false;
    }

    const contentSchema = JSON.parse(content);

    // Content Schema format: { type: "root", version: 1, children: [...] }
    if (contentSchema.type !== "root") {
      return false;
    }

    if (!contentSchema.children || !Array.isArray(contentSchema.children)) {
      return false;
    }

    // Check for significant node types that make content valid
    const significantNodeTypes = [
      NODE_TYPE_INVESTMENT.SELF_CONTRACT,
      NODE_TYPE_INVESTMENT.DECISION,
      NODE_TYPE_INVESTMENT.INVESTMENT_DECISION,
      NODE_TYPE_INVESTMENT.PRICE_TREND,
      "action-item",
      NODE_TYPE_STRUCTURED.CHECKLIST,
      NODE_TYPE_ENTITY.STOCK,
      NODE_TYPE_ENTITY.TAG,
      NODE_TYPE_ENTITY.IMAGE,
      // Rich-text container nodes (valid even when empty, as users may be editing them)
      "heading",
      "list",
      "listitem",
      "quote",
      "code",
    ];

    // Recursively check node tree
    function checkNode(node: unknown, depth = 0): boolean {
      const nodeRecord = asRecord(node);
      if (!nodeRecord) return false;

      // Get node type from node itself (not from node.data)
      const nodeType = asString(nodeRecord.type);

      // Get data content from node.data or node itself
      const dataContent = getNodeData(nodeRecord);

      // Check for significant node type
      if (significantNodeTypes.includes(nodeType)) {
        return true;
      }

      // Check for text node with actual content
      // Content Schema text node: { type: "text", data: { text: "..." } }
      const textContent = asString(dataContent.text) || asString(nodeRecord.text);
      if (nodeType === "text" && textContent && textContent.trim() !== "") {
        return true;
      }

      // Recursively check children
      // Content Schema: children are in data.children or node.children
      const children = getNodeChildren(nodeRecord, dataContent);
      if (children.length > 0) {
        return children.some((child) => checkNode(child, depth + 1));
      }

      return false;
    }

    const result = contentSchema.children.some((child: unknown) => checkNode(child, 1));
    return result;
  } catch (e) {
    console.error("Failed to parse Content Schema:", e);
    return false;
  }
}

/**
 * Validate DecisionNode required fields
 * Backend requires: ticker, decision, actionType, price, quantity, reason, executedAt
 * ONLY supports Content Schema format: { type: "root", version: 1, children: [...] }
 */
function validateDecisionNodes(content: string): ValidationResult {
  try {
    if (!content || content.trim() === "") {
      return { valid: true };
    }

    const contentSchema = JSON.parse(content);

    // Content Schema format: { type: "root", version: 1, children: [...] }
    if (contentSchema.type !== "root" || !contentSchema.children) {
      return { valid: true };
    }

    if (!Array.isArray(contentSchema.children)) {
      return { valid: true };
    }

    function checkDecisionNode(node: unknown): ValidationResult {
      const nodeRecord = asRecord(node);
      if (!nodeRecord) {
        return { valid: true };
      }

      // Content Schema format: data is wrapped in node.data
      const nodeData = getNodeData(nodeRecord);
      const nodeType = asString(nodeData.type);

      if (nodeType !== NODE_TYPE_INVESTMENT.DECISION) {
        // Recursively check children
        const children = getNodeChildren(nodeRecord, nodeData);
        if (children.length > 0) {
          for (const child of children) {
            const result = checkDecisionNode(child);
            if (!result.valid) {
              return result;
            }
          }
        }
        return { valid: true };
      }

      // Validate required fields
      const errors: string[] = [];

      const ticker = asString(nodeData.ticker);
      if (ticker.trim() === "") {
        errors.push("股票代码 (Ticker)");
      }

      const decision = asString(nodeData.decision);
      if (!["BUY", "SELL", "HOLD"].includes(decision)) {
        errors.push("决策类型 (Decision)");
      }

      const actionType = asString(nodeData.actionType);
      if (!["FOLLOW", "OVERRIDE", "NONE"].includes(actionType)) {
        errors.push("行动类型 (Action Type)");
      }

      const price = asNumber(nodeData.price);
      if (price === null || price < 0) {
        errors.push("执行价格 (Price)");
      }

      const quantity = asNumber(nodeData.quantity);
      if (quantity === null || quantity < 0) {
        errors.push("数量 (Quantity)");
      }

      const reason = asString(nodeData.reason);
      if (reason.trim() === "") {
        errors.push("决策理由 (Reason)");
      }

      const executedAt = asString(nodeData.executedAt);
      if (executedAt.trim() === "") {
        errors.push("执行时间 (Executed At)");
      }

      // Validate action type and contractId consistency
      const contractID = asString(nodeData.contractId);
      if (actionType === "FOLLOW" || actionType === "OVERRIDE") {
        if (contractID.trim() === "") {
          errors.push("关联合约 (Contract ID)");
        }
      } else if (actionType === "NONE" && contractID.trim() !== "") {
        errors.push("独立决策不能有关联合约");
      }

      if (errors.length > 0) {
        const tickerForMessage = ticker || "未指定";
        return {
          valid: false,
          reason: `决策节点 $${tickerForMessage} 缺少必填项: ${errors.join(", ")}`,
        };
      }

      // Recursively check children (Decision nodes shouldn't have children, but just in case)
      const children = getNodeChildren(nodeRecord, nodeData);
      if (children.length > 0) {
        for (const child of children) {
          const result = checkDecisionNode(child);
          if (!result.valid) {
            return result;
          }
        }
      }

      return { valid: true };
    }

    // Check all decision nodes in the tree
    for (const child of contentSchema.children) {
      const result = checkDecisionNode(child);
      if (!result.valid) {
        return result;
      }
    }

    return { valid: true };
  } catch (e) {
    console.error("Failed to validate decision nodes:", e);
    return { valid: false, reason: "决策节点验证失败" };
  }
}

/**
 * Validate SelfContractNode required fields
 * Backend requires: ticker, template, expiration, idea, trigger.type, (priceLevel > 0 when trigger is price)
 * ONLY supports Content Schema format: { type: "root", version: 1, children: [...] }
 */
function validateSelfContractNodes(content: string): ValidationResult {
  try {
    if (!content || content.trim() === "") {
      return { valid: true };
    }

    const contentSchema = JSON.parse(content);

    // Content Schema format: { type: "root", version: 1, children: [...] }
    if (contentSchema.type !== "root" || !contentSchema.children) {
      return { valid: true };
    }

    if (!Array.isArray(contentSchema.children)) {
      return { valid: true };
    }

    function checkSelfContractNode(node: unknown): ValidationResult {
      const nodeRecord = asRecord(node);
      if (!nodeRecord) {
        return { valid: true };
      }

      // Content Schema format: data is wrapped in node.data
      const nodeData = getNodeData(nodeRecord);
      const nodeType = asString(nodeData.type);

      if (nodeType !== NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
        // Recursively check children
        const children = getNodeChildren(nodeRecord, nodeData);
        if (children.length > 0) {
          for (const child of children) {
            const result = checkSelfContractNode(child);
            if (!result.valid) {
              return result;
            }
          }
        }
        return { valid: true };
      }

      // Validate required fields
      const errors: string[] = [];

      const ticker = asString(nodeData.ticker);
      if (ticker.trim() === "") {
        errors.push("股票代码 (Ticker)");
      }

      const template = asString(nodeData.template);
      if (!["value-oriented", "trading-oriented"].includes(template)) {
        errors.push("模板类型 (Template)");
      }

      const expiration = asString(nodeData.expiration);
      if (expiration.trim() === "") {
        errors.push("到期日期 (Expiration)");
      }

      const idea = asString(nodeData.idea);
      if (idea.trim() === "") {
        errors.push("投资观点 (Idea)");
      }

      const action = asString(nodeData.action);
      if (action.trim() === "") {
        errors.push("触发后行动 (Action)");
      }

      // Validate trigger type
      const trigger = asRecord(nodeData.trigger);
      if (!trigger) {
        errors.push("触发条件 (Trigger)");
      } else {
        const triggerType = asString(trigger.type);
        if (!triggerType || !["price", "expiration", "time", "custom"].includes(triggerType)) {
          errors.push("触发类型 (Trigger Type)");
        } else if (triggerType === "price") {
          // For price trigger, priceLevel must be > 0
          const priceLevel = asNumber(trigger.priceLevel);
          if (priceLevel === null || priceLevel <= 0) {
            errors.push("价格触发必须大于 0 (Price Level)");
          }
          // Operator should be present for price trigger
          const operator = asString(trigger.operator);
          if (!operator || !["!=", "!<", "!>", "<=", ">=", "<", ">", "=="].includes(operator)) {
            errors.push("价格操作符 (Price Operator)");
          }
        }
      }

      if (errors.length > 0) {
        const tickerForMessage = ticker || "未指定";
        return {
          valid: false,
          reason: `自定约节点 $${tickerForMessage} 缺少必填项: ${errors.join(", ")}`,
        };
      }

      // Recursively check children (Self-Contract nodes shouldn't have children, but just in case)
      const children = getNodeChildren(nodeRecord, nodeData);
      if (children.length > 0) {
        for (const child of children) {
          const result = checkSelfContractNode(child);
          if (!result.valid) {
            return result;
          }
        }
      }

      return { valid: true };
    }

    // Check all self-contract nodes in the tree
    for (const child of contentSchema.children) {
      const result = checkSelfContractNode(child);
      if (!result.valid) {
        return result;
      }
    }

    return { valid: true };
  } catch (e) {
    console.error("Failed to validate self-contract nodes:", e);
    return { valid: false, reason: "自定约节点验证失败" };
  }
}

export const validationService = {
  canSave(state: EditorState): ValidationResult {
    // Check if there's significant content (can be Self-Contract, other special nodes, or text)
    const hasSignificantContent = hasSignificantNodes(state.content);

    // Must have significant content, attachment, or local file
    if (!hasSignificantContent && state.metadata.attachments.length === 0 && state.localFiles.length === 0) {
      return { valid: false, reason: "Content cannot be empty" };
    }

    // Validate all SelfContractNode required fields
    const selfContractValidation = validateSelfContractNodes(state.content);
    if (!selfContractValidation.valid) {
      return selfContractValidation;
    }

    // Validate all DecisionNode required fields
    const decisionValidation = validateDecisionNodes(state.content);
    if (!decisionValidation.valid) {
      return decisionValidation;
    }

    // Cannot save while uploading
    if (state.ui.isLoading.uploading) {
      return { valid: false, reason: "Wait for upload to complete" };
    }

    // Cannot save while already saving
    if (state.ui.isLoading.saving) {
      return { valid: false, reason: "Save in progress" };
    }

    return { valid: true };
  },
};
